import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, EyeOff, Shield, KeyRound, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StaffCredentials = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [revealing, setRevealing] = useState({});
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API}/admin/staff-credentials`);
      setStaffList(res.data);
    } catch { toast.error('Failed to fetch staff credentials'); }
    finally { setLoading(false); }
  };

  const handleReveal = async (userId) => {
    if (revealedPasswords[userId]) {
      setRevealedPasswords(prev => { const copy = { ...prev }; delete copy[userId]; return copy; });
      return;
    }
    setRevealing(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await axios.post(`${API}/admin/staff-credentials/${userId}/reveal`);
      setRevealedPasswords(prev => ({ ...prev, [userId]: res.data.password }));
      // Auto-hide after 15 seconds
      setTimeout(() => {
        setRevealedPasswords(prev => { const copy = { ...prev }; delete copy[userId]; return copy; });
      }, 15000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reveal password');
    } finally {
      setRevealing(prev => ({ ...prev, [userId]: false }));
    }
  };

  const openResetDialog = (staff) => {
    setResetTarget(staff);
    setResetPassword('');
    setResetDialogOpen(true);
  };

  const handleReset = async () => {
    if (!resetTarget || !resetPassword) return;
    setResetting(true);
    try {
      await axios.post(`${API}/admin/staff-credentials/${resetTarget.id}/reset`, {
        new_password: resetPassword
      });
      toast.success(`Password reset for ${resetTarget.full_name}`);
      setResetDialogOpen(false);
      // Clear any revealed password for this user
      setRevealedPasswords(prev => { const copy = { ...prev }; delete copy[resetTarget.id]; return copy; });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Staff Credentials
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">View and manage staff account credentials</p>
        </div>

        <Card data-testid="staff-credentials-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-600" /> Staff Accounts
            </CardTitle>
            <CardDescription>All actions are logged for accountability</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
              </div>
            ) : staffList.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No staff accounts found</p>
            ) : (
              <div className="space-y-3" data-testid="staff-list">
                {staffList.map((staff) => (
                  <div key={staff.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3" data-testid={`staff-row-${staff.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                        <KeyRound className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{staff.full_name}</p>
                        <p className="text-sm text-gray-500">{staff.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Password display */}
                      <div className="min-w-[140px] text-right">
                        {revealedPasswords[staff.id] ? (
                          <code className="text-sm bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-2 py-1 rounded font-mono" data-testid={`revealed-password-${staff.id}`}>
                            {revealedPasswords[staff.id]}
                          </code>
                        ) : (
                          <span className="text-gray-400 tracking-widest text-lg">••••••••</span>
                        )}
                      </div>

                      {/* Show/Hide button */}
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleReveal(staff.id)}
                        disabled={revealing[staff.id]}
                        className="min-w-[100px]"
                        data-testid={`reveal-btn-${staff.id}`}
                      >
                        {revealing[staff.id] ? (
                          'Loading...'
                        ) : revealedPasswords[staff.id] ? (
                          <><EyeOff className="w-4 h-4 mr-1" /> Hide</>
                        ) : (
                          <><Eye className="w-4 h-4 mr-1" /> Show</>
                        )}
                      </Button>

                      {/* Reset button */}
                      <Button size="sm" variant="outline" onClick={() => openResetDialog(staff)} data-testid={`reset-btn-${staff.id}`}>
                        <RotateCcw className="w-4 h-4 mr-1" /> Reset
                      </Button>

                      {/* Active status */}
                      <Badge className={staff.is_active !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                        {staff.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reset Password Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
          <DialogContent data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle>Reset Password for {resetTarget?.full_name}</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <p className="text-sm text-gray-500">
                This will set a new password for <strong>{resetTarget?.email}</strong>. The change will be logged.
              </p>
              <div>
                <Label>New Password</Label>
                <Input
                  type="text"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="Enter new password"
                  data-testid="reset-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleReset} disabled={!resetPassword || resetting} data-testid="reset-confirm-btn">
                {resetting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StaffCredentials;
